import { Box } from "@mui/material";
import Alert from "@mui/material/Alert";
import { DualListItem } from "components/DualListSelector";
import DualListSelectorSearchableForm from "components/DualListSelector/DualListSelectorSearchableForm";
import Loader from "components/Loader";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  AdminListRequestOfBaseFilter,
  GetSkusRequest,
  ListResponseOfSkuListItem,
  SkuContentRule,
  SkuContentRuleSku,
  SkuFilter,
  SkuListItem,
  Skus,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import { SkuFormProps } from "types/sku-related-types";
import translateError from "utils/translateError";

type SkuRuleDetailSkusFormProps = {
  sku: Skus;
  skuContentRule: SkuContentRule;
  readonly?: boolean;
  index: number;
} & SkuFormProps;

const SkuRuleDetailSkusForm = ({
  sku,
  skuContentRule,
  readonly = false,
  index,
  onDirty,
  onClean,
  onSkuUpdated,
}: SkuRuleDetailSkusFormProps) => {
  const { organizationId } = contextStore.useState();
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { skuService } = useGlobalContext();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [options, setOptions] = useState<DualListItem[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DualListItem[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  const skuContentRuleSkuResult = useQuery(
    ["skuRuleDetails", sku.id, skuContentRule.id],
    () =>
      skuService.getSkuContentRuleSkus(
        sku.id!,
        skuContentRule.id,
        AdminListRequestOfBaseFilter.fromJS({ sortField: "sku.sku" })
      )
  );

  const fetchedSkus = useQuery<ListResponseOfSkuListItem>(
    ["skus", organizationId, searchTerm],
    () =>
      skuService.getSkus(
        GetSkusRequest.fromJS({
          organizationId,
          pageNumber: 0,
          pageSize: 20,
          parameters: SkuFilter.fromJS({
            sku: searchTerm === "" ? undefined : searchTerm.toUpperCase(),
          }),
          sortField: "sku",
        })
      )
  );

  const mapSkuListItem = (skus: SkuListItem[]) => {
    return skus.map((s) => new DualListItem(s.id, s.sku));
  };

  const mapSkuContentRuleSku = (skus: SkuContentRuleSku[]) => {
    return skus.map((s) => new DualListItem(s.skuId, s.sku));
  };

  useEffect(() => {
    setOptions(fetchedSkus.data ? mapSkuListItem(fetchedSkus.data.data!) : []);
  }, [fetchedSkus.data, fetchedSkus.data?.data]);

  useEffect(() => {
    setSelectedOptions(
      skuContentRuleSkuResult.data
        ? mapSkuContentRuleSku(skuContentRuleSkuResult.data.data!)
        : []
    );
  }, [skuContentRuleSkuResult.data, skuContentRuleSkuResult.data?.data]);

  const onCleanForm = useCallback(() => {
    setSelectedOptions(
      skuContentRuleSkuResult.data
        ? mapSkuContentRuleSku(skuContentRuleSkuResult.data.data!)
        : []
    );

    onClean && onClean();
  }, [skuContentRuleSkuResult.data, onClean]);

  if (skuContentRuleSkuResult.isLoading) return <Loader />;
  if (skuContentRuleSkuResult.isError) {
    console.log(skuContentRuleSkuResult.error);
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="skuRuleDetailsError" />
        </Alert>
      </Box>
    );
  }

  const onChangeSkuSelected = (selected: DualListItem[]) => {
    selected.sort((a, b) => a.text.localeCompare(b.text));
    setSelectedOptions(selected);
  };

  const onSubmit = async () => {
    try {
      await registerPromise(
        skuService.saveSkuContentRuleSkus(
          sku.id!,
          skuContentRule.id,
          selectedOptions.map((s) => s.id)
        )
      );
      queryClient.invalidateQueries([
        "skuRuleDetails",
        sku.id,
        skuContentRule.id,
      ]);

      onSkuUpdated && onSkuUpdated();
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "ruleUpdatedMessage" }))
      );
    } catch (err) {
      console.log(err);
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  return (
    <>
      <DualListSelectorSearchableForm
        setSearchTerm={setSearchTerm}
        options={options}
        selectedOptions={selectedOptions}
        isFetching={fetchedSkus.isFetching}
        readonly={readonly}
        index={index}
        onSubmit={onSubmit}
        onChange={onChangeSkuSelected}
        onClean={onCleanForm}
        onDirty={onDirty}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default SkuRuleDetailSkusForm;
